import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import RefreshIcon from '@mui/icons-material/Refresh'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import {
  downloadPatientReport,
  getPatientReports,
} from 'src/supportPortal/services/patientReportsService'

const REPORT_TYPE_LABELS = {
  prescription: 'Prescriptions',
  'therapy-report': 'Therapy Reports',
  'screening-report': 'Screening Reports',
}
const REPORT_TYPE_ORDER = ['prescription', 'therapy-report', 'screening-report']

function fmtDate(millis) {
  if (millis === null || millis === undefined) return '—'
  return new Date(millis).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

export default function PatientReportsTab({ patient }) {
  const patientNumber = patient?.patientNumber
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [reports, setReports] = useState([])
  const [downloadingKey, setDownloadingKey] = useState(null)
  const [downloadError, setDownloadError] = useState(null)

  const load = async () => {
    setLoading(true)
    setError(null)
    setDownloadError(null)
    try {
      const items = await getPatientReports(patientNumber)
      setReports(Array.isArray(items) ? items : [])
    } catch (e) {
      setError(e?.message || 'Failed to load reports')
      setReports([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (patientNumber) load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientNumber])

  const grouped = useMemo(() => {
    const byType = {}
    reports.forEach((r) => {
      if (!byType[r.reportType]) byType[r.reportType] = []
      byType[r.reportType].push(r)
    })
    return byType
  }, [reports])

  const handleDownload = async (report) => {
    const key = `${report.reportType}:${report.bookedEventId}`
    setDownloadingKey(key)
    setDownloadError(null)
    try {
      await downloadPatientReport({
        reportType: report.reportType,
        bookedEventId: report.bookedEventId,
      })
    } catch (e) {
      setDownloadError('Could not download that report. Please try again.')
    } finally {
      setDownloadingKey(null)
    }
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Loading reports…
        </Typography>
      </Box>
    )
  }

  if (error) {
    return (
      <Alert
        severity="error"
        action={
          <Button color="inherit" size="small" startIcon={<RefreshIcon />} onClick={load}>
            Retry
          </Button>
        }
      >
        {error}
      </Alert>
    )
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
        <Typography variant="body2" sx={{ color: 'text.secondary' }}>
          Download this patient&apos;s prescription, therapy and screening report PDFs.
        </Typography>
        <Button size="small" startIcon={<RefreshIcon />} onClick={load}>
          Reload
        </Button>
      </Box>

      {downloadError && (
        <Alert severity="error" sx={{ mb: 1.5 }} onClose={() => setDownloadError(null)}>
          {downloadError}
        </Alert>
      )}

      {reports.length === 0 ? (
        <Alert severity="info">No reports available for this patient within your access.</Alert>
      ) : (
        REPORT_TYPE_ORDER.filter((type) => grouped[type]?.length).map((type) => (
          <Box key={type} sx={{ mb: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
              <DescriptionOutlinedIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {REPORT_TYPE_LABELS[type] || type}
              </Typography>
              <Chip label={grouped[type].length} size="small" sx={{ height: 18, fontSize: 11 }} />
            </Box>
            {grouped[type].map((report) => {
              const key = `${report.reportType}:${report.bookedEventId}`
              const isDownloading = downloadingKey === key
              return (
                <Box
                  key={report.reportId || key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    px: 1,
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {fmtDate(report.dateEpochMillis)}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
                      {report.mentorName || report.mentorId || 'Unknown practitioner'}
                    </Typography>
                  </Box>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      isDownloading ? <CircularProgress size={14} /> : <DownloadRoundedIcon />
                    }
                    disabled={isDownloading || !report.bookedEventId}
                    onClick={() => handleDownload(report)}
                  >
                    {isDownloading ? 'Downloading…' : 'PDF'}
                  </Button>
                </Box>
              )
            })}
          </Box>
        ))
      )}
    </Box>
  )
}
